import React, { PropsWithChildren, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import {
  Box,
  Skeleton,
  Table as MUITable,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  useTheme,
} from '@mui/material';

import { TableDataIndex } from '../@types';
import TableHead, { TableHeadProps } from '../TableHead';
import TablePagination from '../TablePagination';
import WTableRow, { TableRowProps } from '../TableRow';

import { TableProps } from './props';
import { DisableContainerStyle, DisabledContentStyle } from './style';

export const Table = <T, I = TableDataIndex>(
  props: PropsWithChildren<TableProps<T, I>>
) => {
  const {
    pagination = true,
    collapsible = false,
    disabled = false,
    disabledView,
    collapsibleColSpan,
    columns = [],
    checked = [],
    data = [],
    loading,
    loadingRows = 5,
    loadingView,
    noDataView,
    footerView,
    headerProps,
    bodyProps,
    footerProps,
    paginationProps,
    topExtraView,
    children,
    onChecked,
    onRowClick,
    tableContainerProps,
    onRowExpand,
    renderExpandView,
    enableTableHeadDivider = true,
    tableStyle,
    ...restProps
  } = props;

  // ================== STATE
  const [checkedState, setCheckedState] = useState<any[]>(checked);

  useDeepCompareEffect(() => setCheckedState(checked), [checked]);

  // ================== HOOKS
  const theme = useTheme();

  // ================== VARIABLES
  const hasData = data.length > 0;
  const hasChecked = checkedState.length > 0;

  // ================== HELPERS
  const getCheckedStatus = (): TableHeadProps['checked'] => {
    if (hasChecked) {
      if (data.length === checkedState.length) return 'all';
      return 'partial';
    }
    return 'none';
  };

  const triggerChange = (val: any[]) => {
    setCheckedState(val);
    onChecked?.(val);
  };

  // ================== EVENTS
  const onHandleCheckAll: TableHeadProps['onCheckedAll'] = (
    state,
    dataIndex
  ) => {
    if (!state) {
      triggerChange([]);
      return;
    }
    triggerChange(
      data.reduce((r: T[], e) => {
        if (e[dataIndex]) {
          r.push(e[dataIndex]);
        }
        return r;
      }, [])
    );
  };

  const onHandleCheckItem: TableRowProps<T>['onChecked'] = (
    record,
    checked,
    meta
  ) => {
    const checkedItem = record ?? meta.data[meta.dataIndex];
    if (checked) {
      triggerChange([...checkedState, checkedItem]);
    } else {
      triggerChange(checkedState.filter((e) => e !== checkedItem));
    }
  };

  // ================== VIEWS
  const renderTableBodyContent = () => {
    if (loading) {
      if (loadingView) return loadingView;
      return [...Array(loadingRows)].map((e, i) => (
        <TableRow key={i}>
          {columns.map((c) => (
            <TableCell
              key={c.key}
              align="center"
              {...c.dataCellProps}
              sx={{ width: c.width, ...c.dataCellProps?.sx }}
            >
              <Skeleton height={30} />
            </TableCell>
          ))}
        </TableRow>
      ));
    }
    if (!hasData) {
      return (
        <TableRow style={{ height: 100 }}>
          <TableCell colSpan={columns.length} align="center">
            {noDataView}
          </TableCell>
        </TableRow>
      );
    }
    return data.map((e, i) => {
      return (
        <WTableRow
          sx={{
            ...tableStyle?.(e),
          }}
          key={i}
          checked={checkedState.includes(e[columns?.[0]?.dataIndex as any])}
          data={e}
          collapsible={collapsible}
          collapsibleColSpan={collapsibleColSpan}
          columns={columns}
          onClick={onRowClick ? (r, e) => onRowClick(r, e, i) : undefined}
          onExpand={onRowExpand ? (r, e) => onRowExpand(r, e, i) : undefined}
          onChecked={onHandleCheckItem}
          renderExpandView={
            renderExpandView ? (r, e) => renderExpandView(r, e, i) : undefined
          }
        />
      );
    });
  };

  return (
    <>
      {topExtraView}
      <TableContainer
        {...tableContainerProps}
        sx={{ position: 'relative', ...tableContainerProps?.sx }}
      >
        <Box>
          {disabled && (
            <Box sx={DisableContainerStyle}>
              {disabledView && (
                <Box sx={DisabledContentStyle}>{disabledView}</Box>
              )}
            </Box>
          )}
          <MUITable
            sx={{ opacity: loading ? 0.4 : 1, ...restProps.sx }}
            {...restProps}
          >
            <TableHead
              columns={columns}
              {...headerProps}
              onCheckedAll={onHandleCheckAll}
              checked={getCheckedStatus()}
              enableDivider={enableTableHeadDivider}
            />
            <TableBody
              {...bodyProps}
              sx={{
                '.MuiTableRow-root > .MuiTableCell-root': {
                  borderBottom: `1px solid ${theme.palette.customColors.border}`,
                },
              }}
            >
              {renderTableBodyContent()}
              {/* extra view with `children` */}
              {children}
            </TableBody>
            {footerView && (
              <TableFooter {...footerProps}>{footerView}</TableFooter>
            )}
          </MUITable>
        </Box>
        {pagination && <TablePagination data={data} {...paginationProps} />}
      </TableContainer>
    </>
  );
};

export type WTableProps<T = any, I = TableDataIndex> = TableProps<T, I>;
export default Table;
